import { Loader2, Plus, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { type JobRun, LogsContainer } from "~/components/job-log";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
} from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import type {
  GetDipBatchChangeRequestsJobsQuery,
  GetDipBatchEvidencesJobsQuery,
  GetDipDetailQuery,
  GetDipRecordComposersQuery,
} from "~/features/dataImportProjects/gqls";
import { useDataImportJobDiagnosis } from "~/features/dataImportProjects/hooks/useDataImportJobDiagnosis";
import { useDipLabels } from "~/features/dataImportProjects/hooks/useDipLabels";
import { useDipPipelineRun } from "~/features/dataImportProjects/hooks/useDipPipelineRun";
import { useDipRunHistory } from "~/features/dataImportProjects/hooks/useDipRunHistory";
import type {
  DipPeekTarget,
  DipPipelineDryRunResultTarget,
  DipProjectInfoUI,
  DipSchemaGraph,
  DipSummaryGroup,
} from "~/features/dataImportProjects/types";
import { toPipelineDryRunResultTarget } from "~/features/dataImportProjects/types";
import { buildPipelineFlow } from "~/features/dataImportProjects/utils/buildFlow";
import { toComposeColumns, toImportColumns } from "~/features/dataImportProjects/utils/transformers";
import { cn } from "~/lib/utils";
import { v2Route } from "~/v2/routes";
import { DiagnosisDialog } from "./DiagnosisDialog";
import { DipDiagnosisBanner } from "./DipDiagnosisBanner";
import { DipLifecycleAction } from "./DipLifecycleAction";
import { DipNodePeek } from "./DipNodePeek";
import { DipOutputSummary } from "./DipOutputSummary";
import { DipPipelineFlow } from "./DipPipelineFlow";
import { DipPipelineStepper } from "./DipPipelineStepper";
import { DipProjectInfo } from "./DipProjectInfo";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";
import { DipRunBanner } from "./DipRunBanner";
import { DipRunHistory } from "./DipRunHistory";
import { DipRunRail } from "./DipRunRail";
import { DipSchemaFlow } from "./DipSchemaFlow";
import { type JobRunLabel, PipelineDryRunResultDialog } from "./PipelineDryRunResultDialog";
import { RecordUpdateTargetsPanel } from "./RecordUpdateTargetsPanel";

interface PresenterProps {
  projectInfo: DipProjectInfoUI;
  summaryGroups: DipSummaryGroup[];
  dataImportProjectId: string;
  batchChangeRequestsJobs: GetDipBatchChangeRequestsJobsQuery["batchChangeRequestsJobs"];
  composers: GetDipRecordComposersQuery["recordComposerDefinitions"];
  batchEvidencesJobs: GetDipBatchEvidencesJobsQuery["batchEvidencesJobs"];
  projectDataSources: NonNullable<GetDipDetailQuery["dataImportProjectsByPk"]>["dataSourceImportProjects"];
  schema: DipSchemaGraph;
  /** Refetch all pipeline queries (drives the Data Pipeline refresh button). */
  onRefresh: () => Promise<unknown>;
}

interface SectionTitleProps {
  title: string;
  checkboxId: string;
  excludeDeactivated: boolean;
  onExcludeDeactivatedChange: (checked: boolean) => void;
}

function SectionTitle({ title, checkboxId, excludeDeactivated, onExcludeDeactivatedChange }: SectionTitleProps) {
  const labels = useDipLabels();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="text-16-bold">{title}</h3>
      <div className="flex items-center gap-2">
        <Checkbox
          id={checkboxId}
          checked={excludeDeactivated}
          onCheckedChange={(checked) => onExcludeDeactivatedChange(checked === true)}
        />
        <Label htmlFor={checkboxId}>{labels.excludeDeactivated}</Label>
      </div>
    </div>
  );
}

export function DataImportProjectPipelinePresenter({
  projectInfo,
  summaryGroups,
  dataImportProjectId,
  batchChangeRequestsJobs,
  composers,
  batchEvidencesJobs,
  projectDataSources,
  schema,
  onRefresh,
}: PresenterProps) {
  const labels = useDipLabels();
  const { t } = useTranslation();
  const { isReadOnly } = useDipLifecycle();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };
  const [excludeDeactivatedImport, setExcludeDeactivatedImport] = useState(true);
  const [excludeDeactivatedCompose, setExcludeDeactivatedCompose] = useState(true);
  // Peek state is mirrored in the URL (peekType/peekId) so it survives refresh, deep-links and
  // browser back/forward. `openPeek` updates both; the effect below reconciles external URL changes.
  const [{ peekType, peekId, peekTab }, setSearchParams] = useTypedSearchParams(v2Route.dataImportProjects.detail);
  const [peek, setPeek] = useState<DipPeekTarget | null>(() =>
    peekType && peekId ? { type: peekType, id: peekId, initialTab: peekTab } : null,
  );

  const openPeek = useCallback(
    (target: DipPeekTarget | null) => {
      setPeek(target);
      setSearchParams((prev) => ({
        ...prev,
        peekType: target?.type,
        peekId: target?.id,
        peekTab: target?.initialTab,
      }));
    },
    [setSearchParams],
  );

  // Sync the peek from the URL on back/forward, refresh or deep-link. Keep the richer in-memory
  // target (recordKind, recordComposerDefinitionId, …) when the URL still points at the same peek.
  useEffect(() => {
    setPeek((current) => {
      if (!peekType || !peekId) return current === null ? current : null;
      if (current && current.type === peekType && current.id === peekId) return current;
      return { type: peekType, id: peekId, initialTab: peekTab };
    });
  }, [peekType, peekId, peekTab]);
  const [railOpen, setRailOpen] = useState(false);
  const [railMode, setRailMode] = useState<"run" | "diagnosis">("run");
  const [logDialogJobRun, setLogDialogJobRun] = useState<JobRun | null>(null);
  const [logDialogJobDone, setLogDialogJobDone] = useState(false);
  const [dryRunResultTarget, setDryRunResultTarget] = useState<DipPipelineDryRunResultTarget | null>(null);
  const { running, runningAction, alert, setAlert, run } = useDipPipelineRun({
    projectId: dataImportProjectId,
    onRefresh,
    setLogDialogJobRun,
  });
  const diagnosis = useDataImportJobDiagnosis(dataImportProjectId);
  const [diagnosisDialogOpen, setDiagnosisDialogOpen] = useState(false);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<(typeof diagnosis.diagnoses)[number] | null>(null);
  const historyRows = useDipRunHistory(dataImportProjectId, diagnosis.diagnoses);
  // The inline history under the pipeline is the run history (pipeline runs); diagnosis history
  // lives in the diagnosis-mode rail tab. The rail filters by its own mode.
  const pipelineHistoryRows = useMemo(() => historyRows.filter((r) => r.kind === "pipeline"), [historyRows]);
  const dryRunResultTargetsByJobRunId = useMemo(() => {
    const targets = new Map<string, DipPipelineDryRunResultTarget>();
    for (const row of pipelineHistoryRows) {
      const target = toPipelineDryRunResultTarget(row);
      if (target) {
        targets.set(row.id, target);
      }
    }
    return targets;
  }, [pipelineHistoryRows]);
  const resolveDryRunResultTarget = useCallback(
    (jobRunId: string) => {
      if (alert?.dryRunResult?.jobRunId === jobRunId) {
        return alert.dryRunResult;
      }
      return dryRunResultTargetsByJobRunId.get(jobRunId) ?? null;
    },
    [alert, dryRunResultTargetsByJobRunId],
  );
  const onOpenLogs = useCallback(
    (jobRunId: string, dryRun?: boolean) => {
      setLogDialogJobDone(false);
      setLogDialogJobRun({
        jobRunId,
        jobName: "DataImportPipelineJob",
        dryRun: dryRun ?? resolveDryRunResultTarget(jobRunId) !== null,
      });
    },
    [resolveDryRunResultTarget],
  );
  const onOpenDiagnosis = useCallback(
    (diagnosisId: string) => {
      setSelectedDiagnosis(diagnosis.diagnoses.find((d) => d.id === diagnosisId) ?? null);
      setDiagnosisDialogOpen(true);
    },
    [diagnosis.diagnoses],
  );
  const diagnosing = diagnosis.status === "invoking" || diagnosis.status === "polling";
  const beJobsForRail = useMemo(
    () => batchEvidencesJobs.filter((j) => j.deactivatedAt == null).map((j) => ({ id: j.id, name: j.name ?? j.id })),
    [batchEvidencesJobs],
  );
  const bcrJobsForRail = useMemo(
    () =>
      batchChangeRequestsJobs.filter((j) => j.deactivatedAt == null).map((j) => ({ id: j.id, name: j.name ?? j.id })),
    [batchChangeRequestsJobs],
  );

  // jobId -> friendly job name for the dry-run result dialog, so it shows the job name
  // instead of a raw UUID. Keyed by job definition id (== run.jobId).
  const dryRunResultJobLabels = useMemo(() => {
    const labels = new Map<string, JobRunLabel>();
    for (const j of [...batchEvidencesJobs, ...batchChangeRequestsJobs]) {
      labels.set(j.id, { name: j.name ?? j.id });
    }
    return labels;
  }, [batchEvidencesJobs, batchChangeRequestsJobs]);

  const importColumns = useMemo(
    () => toImportColumns(batchEvidencesJobs, projectDataSources, dataImportProjectId, !excludeDeactivatedImport),
    [batchEvidencesJobs, projectDataSources, dataImportProjectId, excludeDeactivatedImport],
  );
  const composeColumns = useMemo(
    () =>
      toComposeColumns(
        composers,
        batchEvidencesJobs,
        batchChangeRequestsJobs,
        dataImportProjectId,
        !excludeDeactivatedCompose,
      ),
    [batchChangeRequestsJobs, batchEvidencesJobs, composers, dataImportProjectId, excludeDeactivatedCompose],
  );

  const importFlow = useMemo(() => buildPipelineFlow(importColumns), [importColumns]);
  const composeFlow = useMemo(() => buildPipelineFlow(composeColumns), [composeColumns]);

  return (
    <div
      className={cn(
        "mx-auto max-w-[var(--content-max)] space-y-4 p-8",
        railOpen && "lg:pr-[392px] transition-[padding] duration-300",
      )}
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-dewey-title">{projectInfo.title}</h1>
            {isReadOnly ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
          </div>
          <p className="sr-only">{labels.pageSubtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <DipLifecycleAction />
          <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("feature.dataImportProjects.detail.dataSource.add")}>
            <Button
              size="sm"
              aria-label={t("feature.dataImportProjects.detail.dataSource.add")}
              disabled={isReadOnly}
              onClick={() => {
                if (isReadOnly) return;
                navigate(
                  v2Route.dataImportProjects.detail.dataSourceDesigner.$buildPath({
                    params: { id: dataImportProjectId },
                  }),
                );
              }}
            >
              <Plus />
              <span>{t("model.dataSource.modelName")}</span>
            </Button>
          </DipReadOnlyTooltip>
        </div>
      </header>

      <Card className="space-y-5">
        <div className="grid gap-6 lg:grid-cols-[minmax(280px,0.85fr)_minmax(0,1.15fr)]">
          <DipProjectInfo info={projectInfo} projectId={dataImportProjectId} />
          <DipSchemaFlow graph={schema} />
        </div>
        <div className="border-t border-border-default pt-5">
          <DipOutputSummary groups={summaryGroups} />
        </div>
      </Card>

      <RecordUpdateTargetsPanel dataImportProjectId={dataImportProjectId} />

      <Card className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-element-contextual text-fg-1">{labels["pipeline.title"]}</h2>
            <DipPipelineStepper />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={running || diagnosing}
              onClick={() => {
                setRailMode("diagnosis");
                setRailOpen(true);
              }}
            >
              {t("feature.dataImportProjects.diagnosis.label")}
            </Button>
            <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.run")}>
              <Button
                size="sm"
                disabled={running || diagnosing || isReadOnly}
                onClick={() => {
                  if (isReadOnly) return;
                  setRailMode("run");
                  setRailOpen(true);
                }}
              >
                {t("ui.run")}
              </Button>
            </DipReadOnlyTooltip>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label={t("ui.refresh")}
            >
              <RefreshCw className={cn("size-4", refreshing && "animate-spin")} />
            </Button>
          </div>
        </div>

        <section id="pipeline-import" className="space-y-2 scroll-mt-[72px]">
          <SectionTitle
            title={labels["section.import"]}
            checkboxId="dip-import-exclude-deactivated"
            excludeDeactivated={excludeDeactivatedImport}
            onExcludeDeactivatedChange={setExcludeDeactivatedImport}
          />
          <DipPipelineFlow
            nodes={importFlow.nodes}
            edges={importFlow.edges}
            heightClass="h-[560px]"
            onNodeOpen={openPeek}
          />
        </section>

        <section id="pipeline-compose" className="space-y-2 scroll-mt-[72px]">
          <SectionTitle
            title={labels["section.compose"]}
            checkboxId="dip-compose-exclude-deactivated"
            excludeDeactivated={excludeDeactivatedCompose}
            onExcludeDeactivatedChange={setExcludeDeactivatedCompose}
          />
          <DipPipelineFlow
            nodes={composeFlow.nodes}
            edges={composeFlow.edges}
            heightClass="h-[560px]"
            onNodeOpen={openPeek}
          />
        </section>

        {alert ? (
          <DipRunBanner
            alert={alert}
            onOpenLogs={
              alert.jobRunId ? () => onOpenLogs(alert.jobRunId as string, Boolean(alert.dryRunResult)) : undefined
            }
            onOpenDryRunResult={
              alert.dryRunResult ? () => setDryRunResultTarget(alert.dryRunResult ?? null) : undefined
            }
            onDismiss={() => setAlert(null)}
          />
        ) : null}
        {(diagnosis.status !== "idle" || diagnosis.result != null) && (
          <DipDiagnosisBanner
            status={diagnosis.status}
            result={diagnosis.result}
            onOpenDetail={() => setDiagnosisDialogOpen(true)}
          />
        )}
        <div className="space-y-2">
          <h3 className="text-14-bold">{t("feature.dataImportProjects.v2.run.history.title")}</h3>
          <DipRunHistory
            rows={pipelineHistoryRows}
            onOpenLogs={onOpenLogs}
            onOpenDiagnosis={onOpenDiagnosis}
            onOpenDryRunResult={setDryRunResultTarget}
          />
        </div>
      </Card>
      <DipNodePeek peek={peek} dipId={dataImportProjectId} onClose={() => openPeek(null)} onOpenPeek={openPeek} />
      <DipRunRail
        open={railOpen}
        mode={railMode}
        readOnly={isReadOnly}
        onClose={() => setRailOpen(false)}
        running={running}
        runningAction={runningAction}
        beJobs={beJobsForRail}
        bcrJobs={bcrJobsForRail}
        onRun={run}
        diagnosisStatus={diagnosis.status}
        onRunDiagnosis={() => {
          if (isReadOnly) return;
          // Match v1: opening the diagnosis dialog immediately so it shows the
          // running spinner and then the results (the banner remains as a summary).
          setSelectedDiagnosis(null);
          setDiagnosisDialogOpen(true);
          void diagnosis.invoke();
        }}
        historyRows={historyRows}
        onOpenLogs={onOpenLogs}
        onOpenDiagnosis={onOpenDiagnosis}
        onOpenDryRunResult={setDryRunResultTarget}
      />
      {logDialogJobRun ? (
        <Dialog open onOpenChange={(open) => !open && setLogDialogJobRun(null)}>
          <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
            <DialogHeader>
              <div className="flex flex-wrap items-start justify-between gap-3 pr-6">
                <div>
                  <DialogTitle className="capitalize">{t("model.batchEvidencesJobRun.modelName")}</DialogTitle>
                  <DialogDescription className="mt-1 text-12-regular text-fg-3">
                    {t("model.common.id")}: {logDialogJobRun.jobRunId}
                  </DialogDescription>
                </div>
                {logDialogJobRun.dryRun ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!logDialogJobDone || !resolveDryRunResultTarget(logDialogJobRun.jobRunId)}
                    onClick={() => {
                      const target = resolveDryRunResultTarget(logDialogJobRun.jobRunId);
                      if (target) {
                        setLogDialogJobRun(null);
                        setDryRunResultTarget(target);
                      }
                    }}
                  >
                    {!logDialogJobDone ? <Loader2 className="size-3.5 animate-spin" /> : null}
                    {t("ui.dryRunResult")}
                  </Button>
                ) : null}
              </div>
            </DialogHeader>
            {/* The dialog header owns the fetch-result button, so no `onFetchResult` here. */}
            <LogsContainer
              jobRun={logDialogJobRun}
              dryRun={logDialogJobRun.dryRun}
              onJobDoneChange={setLogDialogJobDone}
            />
          </DialogContent>
        </Dialog>
      ) : null}
      <PipelineDryRunResultDialog
        open={dryRunResultTarget !== null}
        onOpenChange={(open) => !open && setDryRunResultTarget(null)}
        target={dryRunResultTarget}
        jobLabels={dryRunResultJobLabels}
      />
      <DiagnosisDialog
        open={diagnosisDialogOpen}
        readOnly={isReadOnly}
        onClose={() => {
          setDiagnosisDialogOpen(false);
          setSelectedDiagnosis(null);
        }}
        onRerun={() => {
          if (isReadOnly) return;
          void diagnosis.invoke();
        }}
        status={diagnosis.status}
        result={selectedDiagnosis ?? diagnosis.result}
        latestDiagnosis={diagnosis.latestDiagnosis}
        isStale={diagnosis.isStale}
      />
    </div>
  );
}
